import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, CircularProgress } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import RoundedButton from 'components/Shared/RoundedButton';
import { checkEmailExists } from 'redux_store/api/endpoints/users';
import { REQUEST_STATUS } from 'redux_store/constants';
import { useStep1Styles } from './styles';
import cx from 'classnames';

const Step1EmailCheck = ({ onEditUser }) => {
  const classes = useStep1Styles();
  const dispatch = useDispatch();
  const { en: { forms, btn, messages } } = useSelector((state) => state.i18n);
  const { createUserFlow } = useSelector((state) => state.userAccountManagement);
  const { isDistributionSponsor } = useSelector((state) => state.user);

  const [email, setEmail] = useState(createUserFlow.checkedEmail || '');
  const [emailError, setEmailError] = useState('');

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    // Clear errors when user starts typing
    if (emailError) {
      setEmailError('');
    }
  };

  const handleCheckEmail = async () => {

    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }


    dispatch(checkEmailExists({ email: email.trim() }));
  };

  const handleEditExistingUser = () => {
    if (onEditUser && createUserFlow.existingUser) {
      onEditUser(createUserFlow.existingUser);
    }
  };

  const renderStatusMessage = () => {
    const { emailCheckStatus, existingUser, emailError, checkedEmail } = createUserFlow;

    if (emailCheckStatus === REQUEST_STATUS.loading) {
      return (
        <div className={cx(classes.statusMessage, classes.statusChecking)}>
          <CircularProgress size={20} className={classes.statusIcon} />
          <span>{messages?.checkingEmail || 'Checking email availability...'}</span>
        </div>
      );
    }

    if (emailCheckStatus === REQUEST_STATUS.failed) {
      return (
        <div className={cx(classes.statusMessage, classes.statusError)}>
          <ErrorIcon className={classes.statusIcon} />
          <span>{emailError || 'Error checking email. Please try again.'}</span>
        </div>
      );
    }

    if (emailCheckStatus === REQUEST_STATUS.succeeded && existingUser) {
      // Determine if user exists in current account based on source property
      // source === 'currentAccount' means user exists in current account
      // source === 'system' means user exists in system but not in current account
      const userExistsInCurrentAccount = isDistributionSponsor || existingUser.source === 'currentAccount';

      // Choose appropriate button text and message
      const buttonText = userExistsInCurrentAccount
        ? (btn?.editUser || 'Edit User')
        : (btn?.addToAccount || 'Add to Account');

      const statusMessage = userExistsInCurrentAccount
        ? (messages?.userExists?.replace('{email}', existingUser.email) || `User ${checkedEmail} already exists`)
        : (messages?.userExistsAddToAccount?.replace('{email}', existingUser.email) || `User ${checkedEmail} exists in system`);

      return (
        <div className={cx(classes.statusMessage, classes.statusExists)}>
          <WarningIcon className={classes.statusIcon} />
          <span>
            {statusMessage}
          </span>
          <button
            className={classes.editButton}
            onClick={handleEditExistingUser}
          >
            {buttonText}
          </button>
        </div>
      );
    }

    if (emailCheckStatus === REQUEST_STATUS.succeeded && !existingUser) {
      return (
        <div className={cx(classes.statusMessage, classes.statusAvailable)}>
          <CheckCircleIcon className={classes.statusIcon} />
          <span>
            {messages?.emailAvailable || 'Email is available. Click "Next" to continue.'}
          </span>
        </div>
      );
    }

    return null;
  };

  const isLoading = createUserFlow.emailCheckStatus === REQUEST_STATUS.loading;
  const canProceed = createUserFlow.emailCheckStatus === REQUEST_STATUS.succeeded &&
                     !createUserFlow.existingUser;

  return (
    <div className={classes.container}>
      {/* Form Section */}
      <div className={classes.formSection}>
        <h3 className={classes.title}>
          {forms?.step1EmailVerification || 'Step 1: Email Verification'}
        </h3>
        <p className={classes.subtitle}>
          {forms?.checkUserExistsDescription || 'Check if user exists before creating'}
        </p>

        <div className={classes.emailInputContainer}>
          <label className={classes.emailLabel}>
            {forms?.emailAddress || 'Email Address'} *
          </label>
          <input
            type="email"
            className={cx(classes.emailInput, { error: emailError })}
            placeholder={forms?.emailPlaceholder || 'user@example.com'}
            value={email}
            onChange={handleEmailChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isLoading) {
                handleCheckEmail();
              }
            }}
            disabled={isLoading}
          />
          {emailError && (
            <div style={{ color: '#d32f2f', fontSize: '0.875rem', marginTop: '4px' }}>
              {emailError}
            </div>
          )}
        </div>

        <RoundedButton
          variant="contained"
          color="primary"
          onClick={handleCheckEmail}
          disabled={!email.trim() || isLoading}
          isLoading={isLoading}
          className={classes.checkButton}
        >
          {btn?.verifyEmail || 'Verify Email'}
        </RoundedButton>

        {renderStatusMessage()}
      </div>

      {/* Illustration Section */}
      <div className={classes.illustration}>
        {/* Email SVG Icon */}
        <svg
          className={classes.illustrationIcon}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <div className={classes.illustrationText}>
          <strong>
            {forms?.whyCheckFirst || 'Why check first?'}
          </strong>
          {forms?.checkFirstDescription ||
           'Prevents duplicate users and ensures data integrity in your system'}
        </div>
      </div>
    </div>
  );
};

export default Step1EmailCheck;
